import React, { useEffect, useMemo, useState } from 'react';
import { promptsAPI } from '../../services/api';

interface Prompt {
  key: string;
  title: string;
  description: string;
  group: string;
  placeholders: string[];
  default: string;
  content: string;
  isOverridden: boolean;
  updatedAt: string | null;
  updatedBy: { firstName?: string; lastName?: string; email?: string } | null;
}

const PINK = '#1E3A2F';
const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', marginBottom: 12, overflow: 'hidden' };
const btn: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', border: 'none' };

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const naam = (u: Prompt['updatedBy']) =>
  u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '' : '';

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PromptCard: React.FC<{ prompt: Prompt; onSaved: (p: Prompt) => void }> = ({ prompt, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(prompt.content);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => { setValue(prompt.content); }, [prompt.content]);

  const dirty = value !== prompt.content;

  const save = async () => {
    if (!value.trim()) { setMsg({ type: 'err', text: 'De prompt mag niet leeg zijn.' }); return; }
    setSaving(true); setMsg(null);
    try {
      const res = await promptsAPI.update(prompt.key, value);
      onSaved({ ...prompt, content: value, isOverridden: true, updatedAt: res.prompt?.updatedAt || null, updatedBy: res.prompt?.updatedBy || null });
      setMsg({ type: 'ok', text: 'Opgeslagen. Geldt vanaf de volgende generatie voor alle projecten.' });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.response?.data?.error || 'Opslaan mislukt.' });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Weet je zeker dat je deze prompt terugzet naar de standaard? Je aangepaste versie gaat verloren.')) return;
    setSaving(true); setMsg(null);
    try {
      await promptsAPI.reset(prompt.key);
      setValue(prompt.default);
      onSaved({ ...prompt, content: prompt.default, isOverridden: false, updatedAt: null, updatedBy: null });
      setMsg({ type: 'ok', text: 'Teruggezet naar de standaardprompt.' });
    } catch (e: any) {
      setMsg({ type: 'err', text: e?.response?.data?.error || 'Herstellen mislukt.' });
    } finally { setSaving(false); }
  };

  return (
    <div style={card}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a2e' }}>{prompt.title}</span>
            {prompt.isOverridden && (
              <span style={{ fontSize: 11, fontWeight: 700, color: PINK, background: '#F4F6F1', border: '1px solid #d9e2d5', borderRadius: 20, padding: '2px 9px' }}>
                Aangepast
              </span>
            )}
          </div>
          {prompt.description && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>{prompt.description}</div>}
        </div>
        <span style={{ color: '#9ca3af' }}><ChevronIcon open={open} /></span>
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          {prompt.placeholders.length > 0 && (
            <div style={{ marginBottom: 10, fontSize: 12.5, color: '#6b7280' }}>
              Variabelen (worden automatisch ingevuld):{' '}
              {prompt.placeholders.map((p) => (
                <code key={p} style={{ background: '#f1f2f5', borderRadius: 5, padding: '1px 6px', marginRight: 6, color: '#374151' }}>{p}</code>
              ))}
            </div>
          )}
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', minHeight: 320, boxSizing: 'border-box', padding: 14, borderRadius: 8,
              border: '1px solid #d1d5db', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12.5, lineHeight: 1.55, color: '#1a1a2e', resize: 'vertical',
            }}
          />

          {msg && (
            <div style={{ marginTop: 10, fontSize: 13, color: msg.type === 'ok' ? '#1f7a44' : '#b91c1c' }}>{msg.text}</div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving || !dirty}
              style={{ ...btn, background: dirty && !saving ? PINK : '#e5e7eb', color: dirty && !saving ? '#fff' : '#9ca3af', cursor: dirty && !saving ? 'pointer' : 'default' }}>
              {saving ? 'Bezig…' : 'Opslaan'}
            </button>
            {dirty && (
              <button onClick={() => setValue(prompt.content)} disabled={saving}
                style={{ ...btn, background: '#fff', color: '#374151', border: '1px solid #d1d5db' }}>
                Wijzigingen annuleren
              </button>
            )}
            {prompt.isOverridden && (
              <button onClick={reset} disabled={saving}
                style={{ ...btn, background: '#fff', color: '#b91c1c', border: '1px solid #f3c9c9' }}>
                Herstel standaard
              </button>
            )}
            <span style={{ flex: 1 }} />
            {prompt.isOverridden && prompt.updatedAt && (
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                Laatst gewijzigd {fmtDate(prompt.updatedAt)}{naam(prompt.updatedBy) ? ` door ${naam(prompt.updatedBy)}` : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const PromptsPage: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await promptsAPI.list();
        setPrompts(list as Prompt[]);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Kon de prompts niet laden.');
      } finally { setLoading(false); }
    })();
  }, []);

  const onSaved = (updated: Prompt) => {
    setPrompts((prev) => prev.map((p) => (p.key === updated.key ? updated : p)));
  };

  const groups = useMemo(() => {
    const map = new Map<string, Prompt[]>();
    prompts.forEach((p) => {
      if (!map.has(p.group)) map.set(p.group, []);
      map.get(p.group)!.push(p);
    });
    return Array.from(map.entries());
  }, [prompts]);

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Prompts laden…</div>;
  if (error) return <div style={{ padding: 32, color: '#b91c1c' }}>{error}</div>;

  const aangepast = prompts.filter((p) => p.isOverridden).length;

  return (
    <div style={{ padding: 32, maxWidth: 920 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, color: '#1a1a2e' }}>AI-prompts</h1>
      <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
        De systeemprompts die de AI aansturen bij het genereren, per onderdeel van de methode. Wijzigingen gelden
        generiek voor <strong>alle projecten</strong> en gaan in bij de volgende generatie.
      </p>

      <div style={{ background: '#FFF8E6', border: '1px solid #f5e2a8', borderRadius: 10, padding: '12px 16px', marginBottom: 22, fontSize: 13, color: '#7a5b00', display: 'flex', gap: 10 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div>
          Let op: dit is de volledige, technische prompt inclusief de instructies voor het JSON-antwoord. Verander
          de structuur onderaan (de veldnamen tussen accolades) niet, anders kan de app het antwoord niet meer verwerken.
          Variabelen als <code>{'{{...}}'}</code> worden automatisch ingevuld. Twijfel je? Gebruik <em>Herstel standaard</em>.
        </div>
      </div>

      {aangepast > 0 && (
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
          {aangepast} van {prompts.length} prompts {aangepast === 1 ? 'is' : 'zijn'} aangepast t.o.v. de standaard.
        </div>
      )}

      {groups.map(([group, items]) => (
        <div key={group} style={{ marginBottom: 26 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#9ca3af' }}>{group}</h2>
          {items.map((p) => <PromptCard key={p.key} prompt={p} onSaved={onSaved} />)}
        </div>
      ))}
    </div>
  );
};
